import type { ApexProfile } from '@/lib/supabase';

interface ApexLevelLike {
  account_level?: number | null;
  level?: number | null;
  level_prestige?: number | null;
}

export function computeApexAccountLevel(profile?: ApexLevelLike | null): number | null {
  if (!profile) return null;
  if (typeof profile.account_level === 'number') return profile.account_level;
  if (typeof profile.level === 'number') {
    return profile.level + (profile.level_prestige ?? 0) * 500;
  }
  return null;
}

export function buildApexLevelIconUrl(level?: number | null): string | undefined {
  if (typeof level !== 'number' || !Number.isFinite(level)) return undefined;
  return `https://apexlegendsstatus.com/core/level_badge/?level=${level}`;
}

// Manual overrides for legends whose portraits aren't reliably hosted on
// either Mozambique or apexlegendsstatus.com. Bundled locally in /public.
const LEGEND_LOCAL_OVERRIDES: Record<string, string> = {
  'mad maggie': '/images/apex/mad-maggie.webp',
};

function pascalCaseLegendSlug(legendName: string): string {
  return legendName
    .trim()
    .split(/\s+/)
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1).toLowerCase())
    .join('');
}

export function buildApexLegendIconUrl(legendName?: string | null): string | undefined {
  if (!legendName) return undefined;
  const trimmed = legendName.trim();
  if (!trimmed) return undefined;
  const override = LEGEND_LOCAL_OVERRIDES[trimmed.toLowerCase()];
  if (override) return override;
  // apexlegendsstatus.com humanesas is the most complete CDN — covers every
  // legend we've checked from S1 through S29 (Axle, Sparrow, Alter, Conduit, …).
  return `https://apexlegendsstatus.com/assets/legends/humanesas/${pascalCaseLegendSlug(trimmed)}.webp`;
}

// Tertiary fallback. Mozambique covers most older legends but 404s for newer
// ones. Used only if the primary apexlegendsstatus.com URL fails.
export function buildApexLegendIconFallbackUrl(legendName?: string | null): string | undefined {
  if (!legendName) return undefined;
  const slug = legendName.trim().toLowerCase().replace(/\s+/g, '');
  if (!slug) return undefined;
  return `https://api.mozambiquehe.re/assets/icons/${slug}.png`;
}

/** PostgREST returns a 1:1 embed as an object or a one-element array. */
export function pickApexProfile(
  raw: ApexProfile | ApexProfile[] | null | undefined,
): ApexProfile | null {
  if (!raw) return null;
  return Array.isArray(raw) ? raw[0] ?? null : raw;
}

/** The Apex profile of the player's first linked Apex account, if synced. */
export function findApexProfile(
  userGames:
    | readonly { games?: { name?: string | null } | null; apex_profiles?: ApexProfile | ApexProfile[] | null }[]
    | null
    | undefined,
): ApexProfile | null {
  for (const ug of userGames ?? []) {
    if (ug.games?.name !== 'apex-legends') continue;
    const ap = pickApexProfile(ug.apex_profiles);
    if (ap) return ap;
  }
  return null;
}

/** Columns the strip reads; anon-readable on apex_profiles. */
export const APEX_BADGE_COLUMNS =
  'selected_legend, selected_legend_icon, level, level_prestige, account_level, br_rank_name, br_rank_div, br_rank_img, is_banned, last_sync_status, permanently_not_found';
